import React, { useState, useEffect } from 'react';
import { settingsApi, securityApi, aiApi } from '../services/api';
import { Card, Button, Input, Select, Spinner } from '../components/UI';
import { Gear, UploadSimple, Buildings, Tag, Sun, Moon, Desktop, ShieldCheck, Key, Sparkle, Robot, Brain, MagnifyingGlass, FileMagnifyingGlass } from '@phosphor-icons/react';
import { CURRENCIES, setCurrency } from '../utils/currencyFormat';
import { useBranding } from '../context/BrandingContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form, setForm] = useState({});
  const [success, setSuccess] = useState('');
  const branding = useBranding();
  const { preference: themePreference, setTheme } = useTheme();
  const { user, checkAuth } = useAuth();
  const [aiSettings, setAiSettings] = useState(null);

  useEffect(() => {
    settingsApi.get().then(r => { setSettings(r.data); setForm(r.data); }).finally(() => setLoading(false));
    aiApi.getSettings().then(r => setAiSettings(r.data)).catch(() => {});
  }, []);

  const handleAiToggle = async (field, value) => {
    let update;
    if (field === 'ai_enabled') {
      update = { ai_enabled: value };
    } else {
      update = { modules: { ...aiSettings.modules, [field]: value } };
    }
    const res = await aiApi.updateSettings(update);
    setAiSettings(res.data);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setSuccess('');
    try {
      const res = await settingsApi.update({
        company_name: form.company_name,
        company_address: form.company_address,
        phone: form.phone,
        email: form.email,
        currency: form.currency,
        currency_symbol: form.currency_symbol,
        length_unit: form.length_unit,
        area_unit: form.area_unit,
        volume_unit: form.volume_unit,
        weight_unit: form.weight_unit,
        app_name: form.app_name,
        developer_name: form.developer_name,
        developer_url: form.developer_url,
        developer_tagline: form.developer_tagline,
      });
      setSettings(res.data); setForm(res.data);
      setCurrency(res.data.currency, res.data.currency_symbol);
      branding.reload();
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await settingsApi.uploadLogo(formData);
      setForm(prev => ({ ...prev, logo_url: res.data.logo_url }));
      setSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
    } catch (err) { alert(err.response?.data?.detail || 'Failed to upload'); }
    finally { setUploadingLogo(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-heading)' }}>Settings</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Company profile, currency, units & appearance</p>
      </div>

      {success && <div className="bg-[var(--success-bg)] text-[var(--status-active)] px-4 py-2 rounded-lg text-sm font-medium" data-testid="save-success">{success}</div>}

      {/* Security Questions Section */}
      {user && user.role !== 'Super Admin' && !user.is_demo_user && (
        <SecurityQuestionSection isSet={user.security_question_set} onUpdated={checkAuth} />
      )}

      {/* Theme Preference */}
      <Card className="p-5 space-y-4" data-testid="theme-section">
        <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-heading)' }}>
          <Sun className="w-4 h-4" style={{ color: 'var(--primary)' }} /> Appearance
        </h2>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Choose your preferred theme. This setting is stored locally per device.</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: 'light', label: 'Light', icon: Sun, desc: 'Always light' },
            { key: 'system', label: 'System', icon: Desktop, desc: 'Match OS setting' },
            { key: 'dark', label: 'Dark', icon: Moon, desc: 'Always dark' },
          ].map(opt => (
            <button key={opt.key} onClick={() => setTheme(opt.key)}
              className={`p-4 rounded-lg border-2 text-center transition-all ${themePreference === opt.key ? 'shadow-sm' : 'opacity-60 hover:opacity-80'}`}
              style={{
                backgroundColor: 'var(--card-bg)',
                borderColor: themePreference === opt.key ? 'var(--primary)' : 'var(--border)',
              }}
              data-testid={`theme-opt-${opt.key}`}
            >
              <opt.icon className="w-6 h-6 mx-auto mb-2" style={{ color: themePreference === opt.key ? 'var(--primary)' : 'var(--text-muted)' }} weight={themePreference === opt.key ? 'fill' : 'regular'} />
              <p className="text-sm font-semibold" style={{ color: 'var(--text-heading)' }}>{opt.label}</p>
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{opt.desc}</p>
            </button>
          ))}
        </div>
      </Card>

      {/* AI Modules Section */}
      <Card className="p-5 space-y-4" data-testid="ai-settings-section">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-heading)' }}>
            <Sparkle className="w-4 h-4" style={{ color: 'var(--primary)' }} weight="fill" /> AI Modules (Gemini 3.5 Flash)
          </h2>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs" style={{ color: aiSettings?.ai_enabled ? 'var(--status-active)' : 'var(--text-muted)' }}>
              {aiSettings?.ai_enabled ? 'Active' : 'Inactive'}
            </span>
            <div className="relative" data-testid="ai-master-toggle"
              onClick={() => handleAiToggle('ai_enabled', !aiSettings?.ai_enabled)}
              style={{ cursor: 'pointer' }}>
              <div className={`w-11 h-6 rounded-full transition-colors ${aiSettings?.ai_enabled ? 'bg-blue-600' : 'bg-gray-400'}`} />
              <div className={`absolute top-[2px] w-5 h-5 bg-white rounded-full shadow transition-transform ${aiSettings?.ai_enabled ? 'left-[22px]' : 'left-[2px]'}`} />
            </div>
          </label>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          Enable AI-powered features. Toggle individual modules below. Uses Gemini 3.5 Flash via Emergent Universal Key.
        </p>
        {aiSettings?.ai_enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            {[
              { key: 'planning_assistant', label: 'Planning Assistant', icon: Brain, desc: 'Generate schedules, risk analysis, auto-categorize' },
              { key: 'document_review', label: 'Document Review', icon: FileMagnifyingGlass, desc: 'AI-powered submittal & document analysis' },
              { key: 'chat_assistant', label: 'Chat Assistant', icon: Robot, desc: 'Project-aware AI chatbot' },
              { key: 'universal_search', label: 'Universal Search', icon: MagnifyingGlass, desc: 'Search across all modules' },
            ].map(mod => (
              <div key={mod.key} className="flex items-center justify-between p-3 rounded-lg border transition-all"
                style={{
                  borderColor: aiSettings?.modules?.[mod.key] ? 'var(--primary)' : 'var(--border)',
                  backgroundColor: aiSettings?.modules?.[mod.key] ? 'var(--primary-bg)' : 'var(--card-bg)',
                }}
                data-testid={`ai-module-${mod.key}`}>
                <div className="flex items-center gap-2.5">
                  <mod.icon size={20} weight={aiSettings?.modules?.[mod.key] ? 'fill' : 'regular'}
                    style={{ color: aiSettings?.modules?.[mod.key] ? 'var(--primary)' : 'var(--text-muted)' }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-heading)' }}>{mod.label}</p>
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{mod.desc}</p>
                  </div>
                </div>
                <div className="cursor-pointer"
                  onClick={() => handleAiToggle(mod.key, !aiSettings?.modules?.[mod.key])}
                  data-testid={`ai-toggle-${mod.key}`}>
                  <div className="relative">
                    <div className={`w-9 h-5 rounded-full transition-colors ${aiSettings?.modules?.[mod.key] ? 'bg-blue-600' : 'bg-gray-400'}`} />
                    <div className={`absolute top-[2px] w-4 h-4 bg-white rounded-full shadow transition-transform ${aiSettings?.modules?.[mod.key] ? 'left-[18px]' : 'left-[2px]'}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Company Profile */}
          <Card className="p-5 space-y-4">
            <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-heading)' }}>
              <Buildings className="w-4 h-4" style={{ color: 'var(--primary)' }} /> Company Profile
            </h2>
            <Input label="Company Name" value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="company-name" />
            <Input label="Address" value={form.company_address || ''} onChange={(e) => setForm({ ...form, company_address: e.target.value })} data-testid="company-address" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="company-phone" />
              <Input label="Email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="company-email" />
            </div>

            {/* Logo */}
            <div className="space-y-2">
              <label className="block text-sm font-medium" style={{ color: 'var(--text-heading)' }}>Company Logo</label>
              <div className="flex items-center gap-4">
                {form.logo_url && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="Logo" className="w-16 h-16 object-contain rounded" style={{ border: '1px solid var(--border)' }} data-testid="logo-preview" />
                )}
                <div>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-sm transition-colors" style={{ backgroundColor: 'var(--muted)', color: 'var(--text-heading)' }}>
                    <UploadSimple className="w-4 h-4" />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload" />
                  </label>
                </div>
              </div>
            </div>
          </Card>

          {/* Currency & Units */}
          <Card className="p-5 space-y-4">
            <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-heading)' }}>
              <Gear className="w-4 h-4" style={{ color: 'var(--primary)' }} /> Currency & Units
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Currency" value={form.currency || 'USD'} onChange={(e) => {
                const sel = CURRENCIES.find(c => c.code === e.target.value);
                setForm({ ...form, currency: e.target.value, currency_symbol: sel ? sel.symbol : e.target.value });
              }} data-testid="currency">
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                ))}
              </Select>
              <Input label="Currency Symbol" value={form.currency_symbol || '$'} onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })} data-testid="currency-symbol" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Length Unit" value={form.length_unit || 'm'} onChange={(e) => setForm({ ...form, length_unit: e.target.value })} data-testid="length-unit">
                <option value="m">Meters (m)</option>
                <option value="ft">Feet (ft)</option>
              </Select>
              <Select label="Area Unit" value={form.area_unit || 'sqm'} onChange={(e) => setForm({ ...form, area_unit: e.target.value })} data-testid="area-unit">
                <option value="sqm">Sq Meters (sqm)</option>
                <option value="sqft">Sq Feet (sqft)</option>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Volume Unit" value={form.volume_unit || 'cum'} onChange={(e) => setForm({ ...form, volume_unit: e.target.value })} data-testid="volume-unit">
                <option value="cum">Cubic Meters (cum)</option>
                <option value="cuft">Cubic Feet (cuft)</option>
              </Select>
              <Select label="Weight Unit" value={form.weight_unit || 'kg'} onChange={(e) => setForm({ ...form, weight_unit: e.target.value })} data-testid="weight-unit">
                <option value="kg">Kilograms (kg)</option>
                <option value="lbs">Pounds (lbs)</option>
                <option value="ton">Tons (ton)</option>
              </Select>
            </div>
          </Card>
        </div>

        {/* White Label */}
        <Card className="p-5 space-y-4" data-testid="white-label-section">
          <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-heading)' }}>
            <Tag className="w-4 h-4 text-[#7C3AED]" /> White Label Branding
          </h2>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Customize the app name, developer credit, and footer text. Changes appear across the entire application and PDF reports.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Input label="Application Name" value={form.app_name || ''} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder="ProjectERP" data-testid="app-name" />
            <Input label="Developer Name" value={form.developer_name || ''} onChange={(e) => setForm({ ...form, developer_name: e.target.value })} placeholder="SGK Apps" data-testid="developer-name" />
            <Input label="Developer URL" value={form.developer_url || ''} onChange={(e) => setForm({ ...form, developer_url: e.target.value })} placeholder="https://www.sgkapps.com" data-testid="developer-url" />
            <Input label="Developer Tagline" value={form.developer_tagline || ''} onChange={(e) => setForm({ ...form, developer_tagline: e.target.value })} placeholder="A product of VRV Technical & Technology Solutions FZ LLC" data-testid="developer-tagline" />
          </div>
          <div className="rounded-lg p-3 border" style={{ backgroundColor: 'var(--hover-bg)', borderColor: 'var(--card-border)' }} data-testid="branding-preview">
            <p className="text-[10px] uppercase font-bold mb-1" style={{ color: 'var(--text-muted)' }}>Preview</p>
            <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
              Developed by{' '}
              <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{form.developer_name || 'SGK Apps'}</span>
              {form.developer_tagline && <> &middot; {form.developer_tagline}</>}
            </p>
          </div>
        </Card>

        <div className="flex justify-end mt-4">
          <Button type="submit" disabled={saving} data-testid="save-settings-btn">
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ==================== SECURITY QUESTION SECTION ====================
function SecurityQuestionSection({ isSet, onUpdated }) {
  const [questions, setQuestions] = useState([]);
  const [selectedQ, setSelectedQ] = useState('');
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(!isSet);

  useEffect(() => {
    securityApi.getQuestions().then(r => setQuestions(r.data.questions || [])).catch(() => {});
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!selectedQ) { setError('Please select a security question'); return; }
    if (answer.trim().length < 2) { setError('Answer must be at least 2 characters'); return; }
    setSaving(true);
    try {
      await securityApi.setQuestion({ question: selectedQ, answer: answer.trim() });
      setSuccess('Security question saved successfully');
      setAnswer('');
      if (onUpdated) onUpdated();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save security question');
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="security-question-section">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-heading)' }}>
          <ShieldCheck className="w-4 h-4" style={{ color: isSet ? '#10B981' : '#F59E0B' }} weight="fill" />
          Security Question
          {isSet ? (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="sq-status-set">CONFIGURED</span>
          ) : (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="sq-status-notset">NOT SET</span>
          )}
        </h2>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs font-medium px-3 py-1 rounded-md transition-colors"
          style={{ color: 'var(--text-link)', backgroundColor: 'var(--hover-bg)' }}
          data-testid="sq-toggle-btn"
        >
          {expanded ? 'Collapse' : isSet ? 'Change' : 'Set Up'}
        </button>
      </div>

      {!isSet && !expanded && (
        <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--warning-bg, #FEF3C7)', borderColor: 'var(--warning-border, #FDE68A)' }}>
          <p className="text-xs font-medium" style={{ color: '#92400E' }}>
            <Key className="w-3.5 h-3.5 inline mr-1" />
            Please set up a security question to enable password recovery. This is required for your account security.
          </p>
        </div>
      )}

      {expanded && (
        <form onSubmit={handleSave} className="space-y-3" data-testid="sq-form">
          {error && <div className="p-2 rounded-lg text-sm bg-red-50 text-red-600 border border-red-200" data-testid="sq-error">{error}</div>}
          {success && <div className="p-2 rounded-lg text-sm bg-emerald-50 text-emerald-600 border border-emerald-200" data-testid="sq-success">{success}</div>}

          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            {isSet ? 'Update your security question. This will be used for password recovery.' : 'Choose a security question and provide your answer. This will be used if you forget your password.'}
          </p>

          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>Security Question</label>
            <select
              value={selectedQ}
              onChange={(e) => setSelectedQ(e.target.value)}
              required
              className="w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
              style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border)', color: 'var(--text-heading)' }}
              data-testid="sq-select"
            >
              <option value="">-- Select a question --</option>
              {questions.map((q, i) => <option key={i} value={q}>{q}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>Your Answer</label>
            <input
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              required
              minLength={2}
              placeholder="Type your answer (case-insensitive)"
              className="w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
              style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border)', color: 'var(--text-heading)' }}
              data-testid="sq-answer"
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} data-testid="sq-save-btn">
              {saving ? 'Saving...' : isSet ? 'Update Security Question' : 'Save Security Question'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
